import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X, Loader2, ArrowRight } from "lucide-react";
import { useRing } from "./ringContext";
import { useCaps } from "../editor/CapsProvider";
import { designFromPrompt, hasAiEndpoint, V37_DIAMOND_STYLES, type AiPatch, type AiResult } from "./aiDesign";
import type { GrooveConfig, SplitMode } from "../../shared/api-contract";

/** Mirrors MaterialTab — karat is not a shading input, so a metal intent keeps
 * whatever finish a partition already has unless the prompt named one. */
const DEFAULT_FINISH_ID = "polished";

/** How many metal partitions a split mode has (`materials.length` after the
 * viewer resizes it), so a "two-tone yellow gold" patch paints all of them.
 * v42: "multi" is five OR MORE, so this is a FLOOR for it — a 7-partition band
 * asked to change metal by prompt repaints the first five. The truth is always
 * `state.materials.length`; this map only exists because the patch is applied
 * before that state comes back. */
const PARTITION_COUNT: Record<SplitMode, number> = { none: 1, "two-tone": 2, tricolor: 3, quad: 4, multi: 5 };

const EXAMPLES = [
  "3 carat oval on a thin yellow gold cathedral band with a hidden halo",
  "Make the band bezel set with a halo",
  "1.5 ct round solitaire, platinum, six prong",
];

/**
 * "Describe your ring" — StoneAlgo-style floating AI assistant. A sparkle FAB
 * pinned bottom-left of the viewer opens a small card: a prompt box, a
 * "Create my ring" button, a busy state and the result summary. The prompt is
 * turned into a design patch by `designFromPrompt` (Claude endpoint when
 * `VITE_AI_ENDPOINT` is set, otherwise a fully client-side rule parser) and
 * applied through the live `RingBuilderApi` setters. Esc / click-away closes.
 *
 * Ported from `ui/engagement/AiFab.tsx` and retargeted at the merged v37 state:
 * one prompt now drives the whole ring — band geometry, metal, melee run and
 * engraving as well as the centre stone and head. Engagement-half intents
 * attach the head first (`setEngagementEnabled(true)`).
 */
export function AiFab() {
  const { state, catalogs, r, apply, refresh, engagementOn, beginDraft, endDraft } = useRing();
  const caps = useCaps();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AiResult | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Esc + click-away close.
  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") setOpen(false); };
    const onDown = (ev: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  /**
   * Apply a parsed patch through the Ring Builder setters, draft-batched so the
   * ~15 round trips produce one high-resolution rebuild. Field order matters:
   * engagement is attached first (so the head setters have a head), the split
   * mode lands before the per-partition metals, and the v37-only methods are
   * caps-gated — RPC has no timeout, so naming a method an older viewer lacks
   * would hang forever.
   */
  const applyPatch = useCallback(async (patch: AiPatch) => {
    const v37 = caps.ringBuilderV37;
    const wantsHead = !!(patch.centerStone || patch.sideStones || patch.head || patch.engagement);
    const headOn = engagementOn || (wantsHead && v37);

    beginDraft();
    try {
      if (wantsHead && !engagementOn && v37) await apply(r.setEngagementEnabled(true));

      // ── Band half ───────────────────────────────────────────────────────
      if (patch.profileId) await apply(r.setProfile(patch.profileId));
      if (patch.ringSizeUS !== undefined) await apply(r.setRingSize(patch.ringSizeUS));
      if (patch.widthMM !== undefined) await apply(r.setWidth(patch.widthMM));
      if (patch.thicknessMM !== undefined) await apply(r.setThickness(patch.thicknessMM));
      if (patch.edgeStyle) await apply(r.setEdgeStyle(patch.edgeStyle));
      if (patch.bandStyle) await apply(r.setBandStyle(patch.bandStyle));
      if (patch.twist) await apply(r.setTwist(patch.twist));
      if (patch.splitMode) await apply(r.setSplitMode(patch.splitMode));
      if (patch.splitPatternId) await apply(r.setSplitPattern(patch.splitPatternId));

      // Metal + finish, after the split so every new partition is painted.
      if (patch.metalPresetId || patch.finishId) {
        const count = PARTITION_COUNT[patch.splitMode ?? state.splitMode] ?? state.materials.length;
        for (let i = 0; i < count; i++) {
          const cur = state.materials[i];
          const presetId = patch.metalPresetId ?? cur?.presetId;
          if (!presetId) continue;
          await apply(r.setPartitionMaterial(i, presetId, patch.finishId ?? cur?.finishId ?? DEFAULT_FINISH_ID));
        }
        if (headOn && patch.metalPresetId) {
          const head = state.engagement?.head.metal;
          await apply(r.setHeadMetal(patch.metalPresetId, patch.finishId ?? head?.finishId ?? DEFAULT_FINISH_ID));
        }
      }

      if (patch.grooves) {
        const { jointType, seam, edges, ...basic } = patch.grooves;
        if (Object.keys(basic).length) await apply(r.setGrooves(basic));
        // Built key by key: an explicit `jointType: undefined` means "classic"
        // to the viewer, so it must not ride along with a seam/edge toggle.
        const joint: Partial<GrooveConfig> = {};
        if (jointType !== undefined) joint.jointType = jointType;
        if (seam !== undefined) joint.seam = seam;
        if (edges !== undefined) joint.edges = edges;
        if (v37 && Object.keys(joint).length) await apply(r.setJointGrooves(joint));
      }

      if (patch.diamond) {
        const d = { ...patch.diamond };
        if (d.style && !v37 && V37_DIAMOND_STYLES.has(d.style)) delete d.style;
        // Picking a style adopts its catalog defaults, exactly like the Gems
        // tab's chips. The viewer owns the channel cut itself (setDiamond).
        if (d.style && d.style !== "none" && d.style !== state.diamond.style) {
          const info = catalogs.diamondStyles.find((s) => s.id === d.style);
          if (info) {
            if (d.count === undefined) d.count = info.defaultCount;
            if (d.spreadDeg === undefined) d.spreadDeg = info.defaultSpreadDeg;
          }
        }
        if (Object.keys(d).length) await apply(r.setDiamond(d));
      }

      if (patch.engravingText !== undefined) await apply(r.setEngravingText(patch.engravingText));
      if (patch.engravingFontFamily) await apply(r.setEngraving({ fontFamily: patch.engravingFontFamily }));

      // v37 geometry.
      if (v37) {
        if (patch.shape) await apply(r.setRingShape(patch.shape));
        if (patch.division) await apply(r.setDivision(patch.division));
        if (patch.comfortFitMM !== undefined) await apply(r.setComfortFit(patch.comfortFitMM));
      }

      // ── Engagement half ─────────────────────────────────────────────────
      if (headOn) {
        if (patch.engagement) await apply(r.setEngagement(patch.engagement));
        if (patch.centerStone) await apply(r.setCenterStone(patch.centerStone));
        if (patch.sideStones) await apply(r.setSideStones(patch.sideStones));
        if (patch.head) await apply(r.setHead(patch.head));
      }
    } finally {
      endDraft();
    }

    await refresh();
  }, [apply, beginDraft, caps.ringBuilderV37, catalogs, endDraft, engagementOn, r, refresh, state]);

  const run = useCallback(async () => {
    const text = prompt.trim();
    if (!text || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await designFromPrompt(text);
      await applyPatch(res.patch);
      setResult(res);
    } catch (err) {
      console.error("[AiFab] design failed:", err);
      setResult({ patch: {}, summary: "Something went wrong applying that — please try again.", mode: "rule" });
    } finally {
      setBusy(false);
    }
  }, [prompt, busy, applyPatch]);

  return (
    <div ref={rootRef} className="fixed bottom-6 left-6 z-40 flex flex-col items-start gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            className="w-[340px] overflow-hidden rounded-2xl border border-outline-variant/40 bg-surface/95 shadow-2xl backdrop-blur-md"
          >
            <div className="flex items-center justify-between border-b border-outline-variant/30 px-4 py-3">
              <div className="flex items-center gap-2">
                <Sparkles size={15} className="text-primary" />
                <span className="font-display text-[12px] font-semibold uppercase tracking-[0.16em] text-on-surface">
                  Describe your ring
                </span>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="flex h-6 w-6 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-variant/50"
              >
                <X size={14} />
              </button>
            </div>

            <div className="flex flex-col gap-3 p-4">
              <textarea
                ref={inputRef}
                value={prompt}
                onChange={(ev) => setPrompt(ev.target.value)}
                onKeyDown={(ev) => { if (ev.key === "Enter" && (ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); void run(); } }}
                rows={3}
                placeholder="e.g. 2 carat oval on a yellow gold cathedral band with a hidden halo and pavé band"
                className="scroll-thin w-full resize-none rounded-xl border border-outline-variant/50 bg-surface-container-lowest px-3 py-2.5 font-body text-[12.5px] leading-relaxed text-on-surface outline-none transition-colors placeholder:text-on-surface-variant/60 focus:border-primary"
              />

              {!prompt && !result ? (
                <div className="flex flex-col gap-1.5">
                  {EXAMPLES.map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      onClick={() => { setPrompt(ex); inputRef.current?.focus(); }}
                      className="text-left font-body text-[11px] text-on-surface-variant/80 transition-colors hover:text-primary"
                    >
                      &ldquo;{ex}&rdquo;
                    </button>
                  ))}
                </div>
              ) : null}

              <AnimatePresence>
                {result && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5"
                  >
                    <p className="font-body text-[12px] leading-relaxed text-on-surface">{result.summary}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="button"
                onClick={() => void run()}
                disabled={busy || !prompt.trim()}
                data-ai-create
                className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 font-display text-[12px] font-semibold uppercase tracking-[0.12em] text-on-primary transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Designing…
                  </>
                ) : (
                  <>
                    Create my ring
                    <ArrowRight size={14} />
                  </>
                )}
              </button>

              <p className="text-center font-body text-[9.5px] uppercase tracking-[0.14em] text-on-surface-variant/50">
                {hasAiEndpoint() ? "Powered by Claude" : "Instant on-device design"}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        aria-label="Describe your ring"
        data-ai-fab
        onClick={() => { setOpen((o) => !o); }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary text-on-primary shadow-xl shadow-primary/25 ring-1 ring-white/20"
      >
        <motion.span
          animate={open ? {} : { scale: [1, 1.12, 1], rotate: [0, 8, -8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          {open ? <X size={22} /> : <Sparkles size={22} />}
        </motion.span>
      </motion.button>
    </div>
  );
}
